import { useEffect, useMemo, useRef, useState } from 'react';
import { isAlgoliaConfigured, searchCalibres } from '../services/algolia';
import {
  localCalibreMatches,
  orderByHitIds,
  type CalibreSearchFilters,
} from '../services/calibreSearchQuery';
import {
  getCustomCalibres,
  getCustomCalibresByIds,
  type CustomCalibreEntry,
} from '../services/firebase';

/**
 * Long enough that a normal typing burst is one request, short enough that the
 * list feels live. Matches the reference listing and `useBrandSearch`.
 */
const DEFAULT_DEBOUNCE_MS = 250;

/**
 * One page of candidates. The pickers this serves show a scrolling shortlist,
 * not a paginated table, and every hit costs a Firestore document read in the
 * hydration pass — so this is deliberately a shortlist size, not a page size.
 */
const DEFAULT_HITS_PER_PAGE = 50;

export interface UseCalibreSearchOptions {
  /** Skip all work while false — e.g. a picker whose modal is closed. */
  enabled?: boolean;
  /** Server-side scope. Referentially stable, or memoize it: it drives the effect. */
  filters?: CalibreSearchFilters;
  hitsPerPage?: number;
  debounceMs?: number;
  /**
   * Load the whole collection instead of a ranked page.
   *
   * For the case where the caller's own scope CANNOT be expressed as an index
   * filter, so a ranked page would silently under-report: the only brand context
   * available is a legacy free-text `brandName`, which is on the record but not
   * faceted. A global top-N would then be narrowed by the caller's local brand
   * predicate down to a handful of rows — or none — where the old full scan
   * showed the brand's whole calibre list.
   *
   * Deliberately opt-in and narrow. It reinstates exactly the read this hook
   * exists to remove, so it should only ever cover a case the index cannot
   * answer at all. Adding `filterOnly(brandName)` in watchlock would retire it.
   */
  exhaustive?: boolean;
}

export interface UseCalibreSearchResult {
  /** Full Firestore documents, in Algolia's ranked order. */
  results: CustomCalibreEntry[];
  loading: boolean;
  /** True match count for the query, which can exceed `results.length`. */
  totalHits: number;
  /**
   * True when the results came from the Firestore full-collection fallback
   * rather than the index — so a caller can say so instead of implying the
   * shortlist is ranked and complete.
   */
  degraded: boolean;
}

/**
 * Search calibres via Algolia and hand back the matching Firestore documents.
 *
 * Two passes on purpose:
 *
 *  1. Algolia decides WHICH calibres match and in what order. It searches `name`
 *     and `alsoKnownAs` with typo tolerance and accent folding, which is what the
 *     `.includes()` filters these pickers used to run could never do.
 *  2. The hit ids are read back as real documents. The index carries a fraction
 *     of what a picker renders and none of what a picker DECIDES on — no
 *     `aliasDetails`, `isCatchAll`, `data.tiers` (see
 *     `CALIBRE_FIELDS_NOT_INDEXED`) — so the caller must be handed documents, not
 *     hits. `onSelect` handlers pass the entry straight into a write path.
 *
 * That is ~50 document reads per search instead of the ~1,822 these pickers paid
 * on every open, and the reads are of the SAME documents the picker renders.
 *
 * Deprecated calibres (redirect aliases) are dropped after hydration, matching
 * `getCustomCalibres()`'s default. The index does carry `deprecated` now
 * (watchlock #865), so this could become a server-side filter and stop spending
 * reads on rows it throws away — deliberately left alone here, because the
 * post-hydration drop is also what protects against a stale index entry, and
 * changing it is a picker change, not a listing one. A search whose entire page
 * is deprecated therefore renders empty while `totalHits` is non-zero, which is
 * correct — those calibres are not pickable.
 *
 * When Algolia is unconfigured or the search throws, this falls back to the full
 * collection read the pickers used to do and filters it locally. Reporting "no
 * such calibre" because search was down would have the admin create a duplicate.
 * The fallback collection is fetched once per hook instance and reused.
 */
export function useCalibreSearch(
  term: string,
  opts?: UseCalibreSearchOptions,
): UseCalibreSearchResult {
  const enabled = opts?.enabled ?? true;
  const hitsPerPage = opts?.hitsPerPage ?? DEFAULT_HITS_PER_PAGE;
  const debounceMs = opts?.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const exhaustive = opts?.exhaustive ?? false;
  const filters = opts?.filters;

  const [debouncedTerm, setDebouncedTerm] = useState(term);
  const [results, setResults] = useState<CustomCalibreEntry[]>([]);
  const [totalHits, setTotalHits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [degraded, setDegraded] = useState(false);
  /**
   * Guards against out-of-order responses: a slow request for "sw2" must not
   * overwrite the already-arrived result for "sw200-1".
   */
  const requestSeq = useRef(0);
  /** Memoized full-collection fallback, fetched at most once per instance. */
  const fallbackAll = useRef<Promise<CustomCalibreEntry[]> | null>(null);

  useEffect(() => {
    // Clearing the box restores the unfiltered shortlist immediately — waiting
    // out the debounce to show MORE rows just looks like lag.
    if (!term.trim()) {
      setDebouncedTerm(term);
      return;
    }
    const timer = setTimeout(() => setDebouncedTerm(term), debounceMs);
    return () => clearTimeout(timer);
  }, [term, debounceMs]);

  // Serialized so the effect re-runs when a filter VALUE changes without
  // demanding that callers memoize the object they pass.
  const filterKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    if (!enabled) {
      setResults([]);
      setTotalHits(0);
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    const trimmed = debouncedTerm.trim();
    const activeFilters = JSON.parse(filterKey) as CalibreSearchFilters;
    setLoading(true);

    const runFallback = async () => {
      if (!fallbackAll.current) fallbackAll.current = getCustomCalibres();
      const all = await fallbackAll.current;
      // The server-side scope filters can't be reproduced here, so the caller's
      // own predicate is what narrows this — same as it did before Algolia.
      return all.filter((c) => localCalibreMatches(c, trimmed));
    };

    const run = async () => {
      if (exhaustive || !isAlgoliaConfigured()) {
        const rows = await runFallback();
        if (seq !== requestSeq.current) return;
        setResults(rows);
        setTotalHits(rows.length);
        // An exhaustive scan is a deliberate choice, not a failure — only an
        // absent or broken index is worth telling the user about.
        setDegraded(!exhaustive);
        return;
      }
      try {
        const res = await searchCalibres(
          trimmed,
          activeFilters,
          0,
          hitsPerPage,
          { attributes: ['objectID'] },
        );
        if (seq !== requestSeq.current) return;
        const ids = res.hits.map((h) => h.objectID);
        const docs = await getCustomCalibresByIds(ids);
        if (seq !== requestSeq.current) return;
        setResults(
          orderByHitIds(docs, ids).filter((c) => c.deprecated !== true),
        );
        setTotalHits(res.totalHits);
        setDegraded(false);
      } catch (error) {
        // Non-fatal: the full scan is slow and unranked but always correct.
        console.warn('Calibre search unavailable, using a full scan', error);
        const rows = await runFallback();
        if (seq !== requestSeq.current) return;
        setResults(rows);
        setTotalHits(rows.length);
        setDegraded(true);
      }
    };

    run()
      .catch((error) => {
        if (seq !== requestSeq.current) return;
        console.error('Failed to load calibres', error);
        setResults([]);
        setTotalHits(0);
      })
      .finally(() => {
        if (seq === requestSeq.current) setLoading(false);
      });
  }, [enabled, debouncedTerm, filterKey, hitsPerPage, exhaustive]);

  return useMemo(
    () => ({ results, loading, totalHits, degraded }),
    [results, loading, totalHits, degraded],
  );
}
